import React from 'react';
import {
  Box,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Typography,
  Alert,
  Button,
  Stack,
  Chip,
} from '@mui/material';

export type ApiKeyPermissionAccess = 'read' | 'read_write';

export interface ApiKeyPermissionOption {
  path: string;
  label: string;
  description: string;
  /** Whether routes under this path expose write methods (POST/PUT/PATCH/DELETE). */
  access: ApiKeyPermissionAccess;
}

/** Curated endpoint grants matching apiProtection prefix matching. */
export const API_KEY_PERMISSION_OPTIONS: ApiKeyPermissionOption[] = [
  {
    path: '*',
    label: 'Full access',
    description:
      'All API endpoints your account can use — read and write. Prefer narrower grants for production keys.',
    access: 'read_write',
  },
  {
    path: '/products',
    label: 'Product catalog',
    description:
      'Recommended catalog API. Read products, search, stats, and brand/category listing (GET only).',
    access: 'read',
  },
  {
    path: '/cameras',
    label: 'Cameras (legacy)',
    description:
      'Deprecated alias of /products — same catalog data. Still supported for older integrations; new clients should use /products instead. Only enable if something still calls /cameras.',
    access: 'read',
  },
  {
    path: '/store/product-overrides',
    label: 'Store product overrides',
    description:
      'Read and write store-scoped title, description, tags, and pricing overrides used when syncing to commerce platforms.',
    access: 'read_write',
  },
  {
    path: '/store/sku-overrides',
    label: 'SKU overrides',
    description:
      'Read and write store SKU ↔ UPC mappings (list, upsert, import, delete).',
    access: 'read_write',
  },
  {
    path: '/store/bundles',
    label: 'Store bundles',
    description:
      'Read and write store-owned kits composed from Motzu simples (list, create, update, delete).',
    access: 'read_write',
  },
  {
    path: '/store/sync-selection',
    label: 'Sync selection',
    description:
      'Read and update which products are selected to sync to your commerce platform.',
    access: 'read_write',
  },
  {
    path: '/map-pricing',
    label: 'MAP pricing',
    description:
      'Read and write MAP pricing records (list, create, update, import). Scope still limited by your account role.',
    access: 'read_write',
  },
  {
    path: '/integrations',
    label: 'Integrations',
    description:
      'Read and manage ERP / commerce integration settings (Shopify, BigCommerce, etc.).',
    access: 'read_write',
  },
];

export const DEFAULT_STORE_API_KEY_PERMISSIONS = [
  '/products',
  '/store/product-overrides',
];

interface ApiKeyPermissionPickerProps {
  value: string[];
  onChange: (permissions: string[]) => void;
  disabled?: boolean;
}

const accessChipLabel = (access: ApiKeyPermissionAccess): string =>
  access === 'read' ? 'Read' : 'Read & write';

export const ApiKeyPermissionPicker: React.FC<ApiKeyPermissionPickerProps> = ({
  value,
  onChange,
  disabled = false,
}) => {
  const hasFullAccess = value.includes('*');

  const toggle = (path: string) => {
    if (disabled) return;

    if (path === '*') {
      onChange(hasFullAccess ? [...DEFAULT_STORE_API_KEY_PERMISSIONS] : ['*']);
      return;
    }

    if (hasFullAccess) {
      // Switching from full access to a specific grant set
      onChange([path]);
      return;
    }

    if (value.includes(path)) {
      const next = value.filter((p) => p !== path);
      onChange(next.length > 0 ? next : [...DEFAULT_STORE_API_KEY_PERMISSIONS]);
    } else {
      onChange([...value, path]);
    }
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="subtitle2">Endpoint permissions</Typography>
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            disabled={disabled}
            onClick={() => onChange([...DEFAULT_STORE_API_KEY_PERMISSIONS])}
          >
            Store defaults
          </Button>
          <Button size="small" disabled={disabled} onClick={() => onChange(['*'])}>
            Full access
          </Button>
        </Stack>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        Checking a grant allows this API key to call that path prefix with every HTTP method the
        endpoint supports (GET for read; POST/PUT/PATCH/DELETE for write). There is no separate
        read-only toggle — if a grant says Read &amp; write, enabling it adds write access.
        Prefixes match nested routes (e.g. <code>/store/product-overrides</code> covers{' '}
        <code>/store/product-overrides/:productId</code>).
      </Typography>
      {hasFullAccess && (
        <Alert severity="warning" sx={{ mb: 1.5 }}>
          This key can call every endpoint your account can access, including write operations.
        </Alert>
      )}
      <FormGroup>
        {API_KEY_PERMISSION_OPTIONS.map((option) => {
          const checked = hasFullAccess || value.includes(option.path);
          const optionDisabled = disabled || (hasFullAccess && option.path !== '*');
          return (
            <FormControlLabel
              key={option.path}
              disabled={optionDisabled}
              control={
                <Checkbox
                  checked={checked}
                  onChange={() => toggle(option.path)}
                  size="small"
                />
              }
              label={
                <Box sx={{ py: 0.25 }}>
                  <Stack direction="row" alignItems="center" flexWrap="wrap" useFlexGap spacing={0.5}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {option.label}
                    </Typography>
                    <Chip
                      label={option.path}
                      size="small"
                      variant="outlined"
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={accessChipLabel(option.access)}
                      size="small"
                      color={option.access === 'read_write' ? 'warning' : 'default'}
                      variant={option.access === 'read_write' ? 'filled' : 'outlined'}
                      sx={{ height: 20, fontSize: '0.7rem' }}
                    />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" component="div">
                    {option.description}
                  </Typography>
                </Box>
              }
              sx={{ alignItems: 'flex-start', mb: 0.5 }}
            />
          );
        })}
      </FormGroup>
    </Box>
  );
};

export const formatPermissionsSummary = (permissions?: string[] | null): string => {
  if (!permissions || permissions.length === 0) return 'None (blocked)';
  if (permissions.includes('*')) return 'Full access';
  return permissions.join(', ');
};

/** Hover copy for permission chips in API key lists. */
export const getPermissionsChipTooltip = (permissions?: string[] | null): string => {
  if (!permissions || permissions.length === 0) {
    return 'No endpoint grants — this key is Active but cannot call any API routes until you edit permissions and add at least one path (or Full access).';
  }
  if (permissions.includes('*')) {
    return 'Can call every endpoint your account can access, including write operations.';
  }
  return `Allowed path prefixes: ${permissions.join(', ')}`;
};
